import { WATCHLIST_DRAFT_GUIDANCE_EVENT, showWatchlistDraftGuidance } from '../../utils/watchlistDraftGuidance';
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { sectionTitleSx } from '../../styles/typography';
import FileUploader from '../FileUploader';
import { FILE_UPLOAD_RESULT_KINDS } from '../fileUploadResult';
import {
    applyWatchlistRevision,
    fetchLatestWatchlistRevisions,
    fetchWatchlistRevision,
    fetchWatchlistRevisionVersion,
    ignoreWatchlistRevision,
    keepWatchlistPortfolioSecurities,
    processWatchlistFile,
    recreateWatchlistRevision,
    reviewWatchlistFileColumns,
} from '../../utils/ApiCall';
import { watchlistColumnReview } from '../../utils/importColumnReview';
import { watchlistVersionSuffix } from '../../utils/watchlistVersionLabels';
import {
    Snackbar,
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid2,
    Paper,
    Typography,
} from '@mui/material';
import {
    MSI_V2_STAGED_IDENTIFIER_COLUMNS,
    requiredColumnsUploadConfig,
    WATCHLIST_KEY,
} from '../../config/UploadConstant';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { cardClasses } from '../../styles/tailwindClasses';
import { watchlistPanelSx } from '../../styles/watchlistMsiStyles';
import PendingImportCard from '../PendingImportCard';
import { restoredLeadingZerosNote, SkippedRowsDialog } from './SkippedUploadRows';
import WatchlistCompletedResultDialog from './WatchlistCompletedResultDialog';
import {
    canonicalWatchlistPublicationObservation,
    classifyWatchlistPublicationError,
    dispatchWatchlistRevisionClosedEvent,
    isDuplicateOfCurrentWatchlistRevision,
    parseWatchlistDeltaClassification,
    WATCHLIST_DELTA_CLASSIFICATIONS,
    WATCHLIST_HISTORY_OPEN_EVENT,
    WATCHLIST_PUBLICATION_CONTRACT_ERROR_CODE,
    WATCHLIST_PUBLICATION_VERIFICATION_MESSAGE,
    WATCHLIST_REVISION_PROGRESS_EVENT,
    WATCHLIST_REVISION_PROGRESS_OBSERVED_EVENT,
    scopeSecurityOperationEventDetail,
} from '../../utils/securityResolutionEvents';
import { useSecurityOperationContext } from '../../context/SecurityOperationContext';
import { useWatchlistApplyFactorSourceReady } from '../../context/WatchlistApplyFactorSourceContext';
import { parseWatchlistPublicationReceipt } from '../../utils/watchlistPublication';
import { normalizeWatchlistLifecycle } from '../../utils/watchlistLifecycle';
import { isNothingMatched } from '../../utils/watchlistImportOutcome';

const WATCHLIST_PUBLICATION_VERIFICATION_ERROR_CODE = (
    'watchlist_publication_verification_pending'
);
// Apply's answer when the Watchlist changed after the review was prepared.
const WATCHLIST_STATE_STALE_CODE = 'watchlist_state_stale';
const REVIEW_UPDATED_NOTICE = (
    'Your Watchlist changed after this review was prepared, so the changes below have been updated.'
);
const APPLY_AGAIN_NOTICE = (
    'Your Watchlist changed while you were reviewing, so this review was prepared again. '
    + 'Check the changes, then click Apply.'
);
// The backend's readiness message for a review the Watchlist moved past.
const OUTDATED_REVIEW_MESSAGE = (
    'The published Watchlist changed since this review was prepared, '
    + 'so it must be prepared again before it can be applied.'
);
// Apply's answer while new factor data is still being published.
const WATCHLIST_FACTOR_SOURCE_UNAVAILABLE_CODE = 'watchlist_factor_source_unavailable';
const FACTOR_SOURCE_UNAVAILABLE_MESSAGE = (
    "New factor data is still being published, so this change can't be applied yet. "
    + 'Your review is kept. Try again in a few minutes.'
);

// The securities a review shows in each section, by identifier and country.
function reviewChanges(review) {
    const details = review?.diff_details_json || {};
    const shown = (buckets) => buckets
        .flatMap((bucket) => Object.values(details[bucket] || {}))
        .map((entry) => {
            const row = entry?.after || entry?.before || {};
            const country = row.selected_country || row.uploaded_country_hint || '';
            const fields = (entry?.changed_fields || []).join(',');
            return `${row.inbound_id_type}:${row.inbound_id_value}|${country}|${fields}`;
        })
        .sort();
    return JSON.stringify([
        shown(['added', 'restored_from_history']),
        shown(['removed']),
        shown(['identifier_changed', 'country_changed', 'attributes_changed']),
    ]);
}

// Apply may prepare a review again unless it is already accepted or carries
// its own approval (a context refresh or a provider choice cannot be amended).
// A removal review is prepared again when it still names the same securities.
function recreatableOnApply(review) {
    return stagedUploadPendingReview(review)
        && review?.draft_readiness?.can_amend !== false;
}

/**
 * Prepares a review the Watchlist moved past again against the current
 * Watchlist. Resolves to the review to show and whether its changes differ,
 * or to no review when the change now matches the Watchlist. `cache` (a ref)
 * shares one attempt per review between callers; a failed one is retried.
 */
function recreateOutdatedReview(review, cache) {
    const revisionId = String(review?.id || review?.watchlist_revision_id || '');
    if (!revisionId) return Promise.reject(new Error('This review could not be updated. Try again.'));
    if (cache.current.revisionId === revisionId) return cache.current.promise;
    const promise = (async () => {
        const result = await recreateWatchlistRevision(revisionId);
        if (result?.success === false) {
            throw new Error(result.message || 'This review could not be updated. Try again.');
        }
        const newId = result?.watchlist_revision_id || result?.pending_revision?.id;
        if (!newId) {
            return {
                review: null,
                message: result?.message || 'This saved change now matches your Watchlist.',
            };
        }
        const recreated = normalizeUploadReview(await fetchWatchlistRevision(newId));
        if (!recreated) {
            throw new Error('Could not load the updated review. Reopen Watchlist History.');
        }
        return {
            review: recreated,
            changed: reviewChanges(recreated) !== reviewChanges(review),
        };
    })();
    cache.current = { revisionId, promise };
    promise.catch(() => {
        if (cache.current.promise === promise) cache.current = { revisionId: '', promise: null };
    });
    return promise;
}

function watchlistApplyError(errorLike, fallbackMessage) {
    const classified = classifyWatchlistPublicationError(errorLike, fallbackMessage);
    const error = new Error(classified.message);
    if (classified.verificationPending) {
        error.code = WATCHLIST_PUBLICATION_VERIFICATION_ERROR_CODE;
    }
    return error;
}

function dispatchWatchlistRevisionProgressEvent(
    payload,
    sessionScopeToken,
    eventName = WATCHLIST_REVISION_PROGRESS_EVENT,
) {
    if (
        typeof window === 'undefined'
        || isDuplicateOfCurrentWatchlistRevision(payload)
    ) {
        return;
    }
    const detail = scopeSecurityOperationEventDetail(payload, sessionScopeToken);
    if (!detail) return;
    window.dispatchEvent(new CustomEvent(eventName, {
        detail,
    }));
}

function dispatchWatchlistHistoryOpenEvent(revisionLink, sessionScopeToken) {
    if (typeof window === 'undefined' || !revisionLink?.revisionId) {
        return;
    }
    const detail = scopeSecurityOperationEventDetail({
        revisionId: revisionLink.revisionId,
        revisionSeq: revisionLink.revisionSeq,
    }, sessionScopeToken);
    if (!detail) return;
    window.dispatchEvent(new CustomEvent(WATCHLIST_HISTORY_OPEN_EVENT, { detail }));
}

function newAttemptId(prefix) {
    if (typeof crypto === 'undefined' || typeof crypto.randomUUID !== 'function') {
        throw new Error('This browser cannot create a secure operation ID. Please update your browser.');
    }
    return `${prefix}-${crypto.randomUUID()}`;
}

function newUploadAttemptId() {
    if (typeof crypto === 'undefined' || typeof crypto.randomUUID !== 'function') {
        throw new Error('This browser cannot create a secure upload attempt ID. Please update your browser.');
    }
    return crypto.randomUUID();
}

function uploadChangedCount(counts) {
    return Number(counts?.changed || 0)
        + Number(counts?.identifier_changed || 0)
        + Number(counts?.country_changed || 0)
        + Number(counts?.attributes_changed || 0);
}

// What the Watchlist grid's "+ Add security" Save stages: an additions batch,
// an identifier to resolve on Apply, or the pending change those amended.
const STAGED_ADDITION_ACTIONS = new Set(['manual_add_batch', 'manual_add_unresolved_identifier']);
const AMENDING_ADDITION_KINDS = new Set(['addition', 'identifier_addition']);

function isStagedWatchlistAddition(metadata) {
    return STAGED_ADDITION_ACTIONS.has(metadata?.action)
        || (metadata?.action === 'combined_draft'
            && AMENDING_ADDITION_KINDS.has(metadata?.draft_edit_kind)
            && metadata?.combined_draft_contract === 'watchlist-combined-draft.v1');
}

function normalizeUploadReview(candidate) {
    if (!candidate || typeof candidate !== 'object') {
        return null;
    }
    const metadata = candidate.source_metadata_json || {};
    const removalReview = metadata.action === 'security_resolution_delete'
        && metadata.membership_removal_review_contract === 'watchlist-membership-removal-review.v1';
    const source = metadata.event_type === 'watchlist_upload_v2_staged' || removalReview
        ? 'upload'
        : candidate.source || candidate.change_source || null;
    const status = String(candidate.status || '').toLowerCase();
    if (source !== 'upload' || (status && status !== 'pending_apply')) {
        return null;
    }
    const lifecycle = normalizeWatchlistLifecycle(candidate.lifecycle);
    const diffSummary = candidate.diff_summary_json || {};
    const counts = diffSummary.counts || {};
    return {
        ...candidate,
        id: candidate.id || candidate.watchlist_revision_id,
        watchlist_revision_id: candidate.watchlist_revision_id || candidate.id,
        source: 'upload',
        change_source: candidate.change_source || 'upload',
        lifecycle,
        diff_summary_json: {
            ...diffSummary,
            counts: {
                ...counts,
                changed: uploadChangedCount(counts),
                unchanged: Number(counts.unchanged ?? counts.retained ?? 0),
            },
        },
    };
}

function extractUploadReview(response) {
    const review = normalizeUploadReview(
        response?.staged_review
        || response?.pending_revision
        || response?.pending_review
        || response
    );
    // A fresh staging response carries the skipped rows beside the review; a
    // reopened review reads them from its stored metadata.
    if (review && response?.input_validation && !review.source_metadata_json?.input_validation) {
        return {
            ...review,
            source_metadata_json: {
                ...(review.source_metadata_json || {}),
                input_validation: response.input_validation,
            },
        };
    }
    return review;
}

function isMsiV2StagedUpload(review) {
    if (!review || typeof review !== 'object') return false;
    try {
        return normalizeWatchlistLifecycle(review.lifecycle) != null;
    } catch (_error) {
        return false;
    }
}

function stagedUploadRetryable(review) {
    if (!review || typeof review !== 'object') return false;
    return normalizeWatchlistLifecycle(review.lifecycle).retry.classification
        === 'retry_pending';
}

function stagedUploadPendingReview(review) {
    if (!review || typeof review !== 'object') return false;
    return normalizeWatchlistLifecycle(review.lifecycle).status === 'pending_review';
}

// Approving a change pins the latest factor data, except when it keeps the
// published Watchlist's factor data (a targeted addition), is a removal
// review, or matches the Watchlist (Apply then only closes it).
function approvalPinsLatestFactorData(review) {
    const metadata = review?.source_metadata_json || {};
    return isMsiV2StagedUpload(review)
        && stagedUploadPendingReview(review)
        && !review.duplicate_of_current
        && !metadata.duplicate_of_current
        && metadata.targeted_addition_contract !== 'watchlist-targeted-addition.v1'
        && metadata.membership_removal_review_contract
            !== 'watchlist-membership-removal-review.v1';
}

function stagedResolutionCounts(review) {
    const lifecycle = normalizeWatchlistLifecycle(review?.lifecycle);
    const rows = lifecycle.progress.candidate_count;
    const processed = Math.floor(lifecycle.progress.completed_credit_units / 3);
    return {
        rows,
        resolved: processed,
        ambiguous: 0,
        unresolved: 0,
        labelsPending: 0,
        duplicates: 0,
        processed,
        pending: Math.max(0, rows - processed),
        progress: lifecycle.progress.percent,
    };
}

export default function WatchlistUploader({
    defaultTargetVolPct,
    defaultTargetVolDollar,
    defaultScaler,
    default_target_vol_pct,
    default_target_vol_dollar,
    default_scaler,
    variant = 'panel',
    onInteraction,
    onUploadBusyChange,
    externalReviewHandoff = null,
    onExternalReviewHandoffConsumed,
    active = true,
    reviewOnly = false,
    onReviewIgnored,
    onReviewRevisionChanged,
    onReviewAlreadyCurrent,
}) {
    const { sessionScopeToken } = useSecurityOperationContext();
    const applyFactorSourceReady = useWatchlistApplyFactorSourceReady();
    const columnReviewActiveRef = useRef(false);
    const watchlistRequiredColumns = requiredColumnsUploadConfig[WATCHLIST_KEY];
    const resolvedDefaultTargetVolPct = defaultTargetVolPct ?? default_target_vol_pct;
    const resolvedDefaultTargetVolDollar = defaultTargetVolDollar ?? default_target_vol_dollar;
    const resolvedDefaultScaler = defaultScaler ?? default_scaler;
    const [duplicateSedols, setDuplicateSedols] = useState([]);
    const [snackBarData, setSnackBarData] = useState({ open: false, message: '', severity: 'info' });
    // The snackbar's version number is read when shown, from the revision id,
    // so it matches Watchlist History. A change that isn't published has none.
    const [snackBarVersion, setSnackBarVersion] = useState({ revisionId: '', suffix: '' });
    const snackBarRevisionId = String(snackBarData?.revisionLink?.revisionId || '').trim();
    const [skippedRowsDialog, setSkippedRowsDialog] = useState({ open: false, payload: null });
    const [completedResult, setCompletedResult] = useState(null);
    const resultScopeRef = useRef(sessionScopeToken);
    resultScopeRef.current = sessionScopeToken;
    const showCompletedResult = (result) => {
        if (resultScopeRef.current === sessionScopeToken) setCompletedResult(result);
    };
    useEffect(() => { setCompletedResult(null); }, [sessionScopeToken]);
    const [pendingUploadReview, setPendingUploadReview] = useState(null);
    const [draftRefresh, setDraftRefresh] = useState(0);
    useEffect(() => {
        const refresh = (event) => {
            if (event.detail?.sessionScopeToken === sessionScopeToken) setDraftRefresh((value) => value + 1);
        };
        window.addEventListener(WATCHLIST_DRAFT_GUIDANCE_EVENT, refresh);
        return () => window.removeEventListener(WATCHLIST_DRAFT_GUIDANCE_EVENT, refresh);
    }, [sessionScopeToken]);
    const [uploadReviewDialogOpen, setUploadReviewDialogOpen] = useState(false);
    const [uploadReviewBusy, setUploadReviewBusy] = useState(false);
    const [uploadReviewError, setUploadReviewError] = useState('');
    // What the last action did to the change under review, shown in the dialog.
    const [uploadReviewNotice, setUploadReviewNotice] = useState(null);
    useEffect(() => {
        if (!uploadReviewDialogOpen) setUploadReviewNotice(null);
    }, [uploadReviewDialogOpen]);
    const pendingUploadAttemptIdRef = useRef(null);
    const lastExternalReviewCommandRef = useRef(null);
    const reviewRecreateRef = useRef({ revisionId: '', promise: null });
    // Read on every render, so Apply turns back on as soon as a later context
    // poll reports the new factor data published.
    const factorSourceBlocksApply = applyFactorSourceReady === false
        && approvalPinsLatestFactorData(pendingUploadReview);

    useEffect(() => {
        if (!snackBarRevisionId) return undefined;
        let cancelled = false;
        fetchWatchlistRevisionVersion(snackBarRevisionId)
            .then((version) => {
                if (cancelled) return;
                setSnackBarVersion({
                    revisionId: snackBarRevisionId,
                    suffix: version?.success === false ? '' : watchlistVersionSuffix(version),
                });
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [snackBarRevisionId]);

    useEffect(() => {
        if (
            Array.isArray(duplicateSedols) &&
            duplicateSedols.length > 0
        ) {
            setSnackBarData({ open: true, message: `Duplicate identifiers detected: ${duplicateSedols.join(', ')}`, severity: 'warning' });
        }
    }, [duplicateSedols]);

    useEffect(() => {
        if (reviewOnly || ['optimizer_restoration', 'watchlist_removal', 'watchlist_addition', 'watchlist_context_refresh'].includes(
            externalReviewHandoff?.origin,
        )) return undefined;
        let cancelled = false;
        async function recoverPendingUploadReview() {
            try {
                const latest = await fetchLatestWatchlistRevisions();
                if (!latest || typeof latest !== 'object' || latest.success === false) {
                    throw new Error(
                        latest?.message || 'Pending Watchlist review is unavailable.',
                    );
                }
                const pendingHeader = latest?.pending_revision;
                const pendingDetail = pendingHeader?.id && !pendingHeader?.diff_details_json
                    ? await fetchWatchlistRevision(pendingHeader.id)
                    : pendingHeader;
                if (pendingDetail?.success === false) {
                    throw new Error(
                        pendingDetail.message || 'Pending Watchlist review is unavailable.',
                    );
                }
                const review = extractUploadReview(pendingDetail || pendingHeader);
                if (!cancelled && review) {
                    if (
                        stagedUploadPendingReview(review)
                        || stagedUploadRetryable(review)
                    ) {
                        // A review the Watchlist moved past is prepared again
                        // before it is shown; if that fails it shows as it is.
                        const recreate = review.draft_readiness?.can_recreate;
                        const outcome = recreate
                            ? await recreateOutdatedReview(review, reviewRecreateRef)
                                .catch((error) => ({ review, error }))
                            : { review };
                        if (cancelled) return;
                        showRecreatedReview(outcome);
                        if (!outcome.review) return;
                        if (recreate) setUploadReviewError(outcome.error?.message || '');
                        if (!columnReviewActiveRef.current) setUploadReviewDialogOpen(true);
                    }
                }
            } catch (err) {
                if (!cancelled) {
                    setSnackBarData({
                        open: true,
                        message: `Could not recover pending Watchlist review: ${
                            err instanceof Error ? err.message : String(err)
                        }`,
                        severity: 'error',
                    });
                }
            }
        }
        recoverPendingUploadReview();
        return () => {
            cancelled = true;
        };
    }, [draftRefresh]);

    useEffect(() => {
        if (!active) return undefined;
        const commandId = Number(externalReviewHandoff?.commandId);
        if (
            !Number.isInteger(commandId)
            || commandId < 1
            || lastExternalReviewCommandRef.current === commandId
        ) return undefined;
        lastExternalReviewCommandRef.current = commandId;
        if (
            !sessionScopeToken
            || externalReviewHandoff?.sessionScopeToken !== sessionScopeToken
        ) {
            onExternalReviewHandoffConsumed?.(commandId);
            return undefined;
        }
        if (externalReviewHandoff?.origin === 'watchlist_context_refresh') {
            onExternalReviewHandoffConsumed?.(commandId);
            return undefined;
        }

        const expectedRevisionId = String(
            externalReviewHandoff?.revisionId || '',
        ).trim();
        if (!expectedRevisionId) {
            onExternalReviewHandoffConsumed?.(commandId);
            return undefined;
        }
        let cancelled = false;
        let completed = false;
        async function openStagedReview() {
            const isOptimizerRestoration = (
                externalReviewHandoff?.origin === 'optimizer_restoration'
            );
            const isWatchlistRemoval = externalReviewHandoff?.origin === 'watchlist_removal';
            const isWatchlistAddition = externalReviewHandoff?.origin === 'watchlist_addition';
            const isPortfolioImport = externalReviewHandoff?.origin === 'portfolio_import';
            const fetchOwnedRevision = isOptimizerRestoration || isWatchlistRemoval
                || isWatchlistAddition || isPortfolioImport;
            try {
                const response = fetchOwnedRevision
                    ? await fetchWatchlistRevision(expectedRevisionId)
                    : externalReviewHandoff?.pendingRevision;
                if (cancelled) return;
                const responseRevisionId = String(
                    response?.id || response?.watchlist_revision_id || '',
                ).trim();
                if (
                    response?.success === false
                    || responseRevisionId !== expectedRevisionId
                    || externalReviewHandoff?.status !== 'changes_awaiting_review'
                    || (isPortfolioImport
                        ? (!externalReviewHandoff.equivalentPending
                            && response?.source_metadata_json?.action !== 'portfolio_import_watchlist_additions')
                        : isOptimizerRestoration
                        ? response?.source_metadata_json?.action !== 'optimizer_saved_portfolio_add_back'
                        : isWatchlistRemoval
                            ? !(response?.source_metadata_json?.action === 'security_resolution_delete'
                                || (response?.source_metadata_json?.action === 'combined_draft'
                                    && response?.source_metadata_json?.draft_edit_kind === 'removal'
                                    && response?.source_metadata_json?.combined_draft_contract === 'watchlist-combined-draft.v1'))
                            : isWatchlistAddition
                                ? !isStagedWatchlistAddition(response?.source_metadata_json)
                                : response?.source_metadata_json?.ingress_kind !== 'm365_excel')
                ) {
                    throw new Error('invalid staged Watchlist review');
                }
                const portfolioLifecycle = isPortfolioImport ? normalizeWatchlistLifecycle(response) : null;
                if (portfolioLifecycle && portfolioLifecycle.status !== 'pending_review') {
                    // Equivalent work may already be approved or finish between
                    // staging and this read. Observe the owned durable revision
                    // through the same publication coordinator as normal Apply.
                    setPendingUploadReview(null);
                    setUploadReviewDialogOpen(false);
                    if (portfolioLifecycle.status === 'unchanged' || isNothingMatched(portfolioLifecycle)) {
                        onReviewAlreadyCurrent?.();
                        return;
                    }
                    if (portfolioLifecycle.status === 'succeeded') {
                        dispatchTerminalWatchlistPublicationObservation(portfolioLifecycle);
                        return;
                    }
                    dispatchWatchlistRevisionProgressEvent({
                        ...portfolioLifecycle, watchlist_revision_id: responseRevisionId,
                        revision_seq: response.revision_seq,
                        reason: 'portfolio_import_existing_watchlist_work',
                    }, sessionScopeToken);
                    return;
                }
                const review = normalizeUploadReview(isPortfolioImport ? { ...response, source: 'upload' } : response);
                if (!review || !stagedUploadPendingReview(review)) {
                    throw new Error('staged Watchlist review is no longer pending');
                }
                setPendingUploadReview(review);
                setUploadReviewDialogOpen(true);
                setUploadReviewError('');
                dispatchWatchlistRevisionProgressEvent({
                    lifecycle: review.lifecycle,
                    pending_revision: review,
                    watchlist_revision_id: responseRevisionId,
                    revision_seq: review.revision_seq,
                    reason: isPortfolioImport
                        ? 'portfolio_import_watchlist_review_handoff'
                        : isOptimizerRestoration
                        ? 'optimizer_watchlist_restoration_review_handoff'
                        : isWatchlistRemoval ? 'watchlist_removal_review_handoff'
                            : isWatchlistAddition ? 'watchlist_addition_review_handoff'
                                : 'watchlist_m365_sync_review_handoff',
                }, sessionScopeToken);
            } catch (_error) {
                if (cancelled) return;
                setPendingUploadReview(null);
                setUploadReviewDialogOpen(false);
                setUploadReviewError('');
                setSnackBarData({
                    open: true,
                    message: fetchOwnedRevision
                        ? 'Could not open the staged Watchlist change. Open Imports to review it.'
                        : 'Could not open the pending Watchlist review. Close and reopen Imports, then click Sync from Excel again.',
                    severity: 'error',
                });
            } finally {
                if (!cancelled) {
                    completed = true;
                    onExternalReviewHandoffConsumed?.(commandId);
                }
            }
        }
        void openStagedReview();
        return () => {
            cancelled = true;
            if (!completed && lastExternalReviewCommandRef.current === commandId) {
                lastExternalReviewCommandRef.current = null;
            }
        };
    }, [
        active,
        externalReviewHandoff,
        onExternalReviewHandoffConsumed,
        sessionScopeToken,
    ]);

    const handleSnackbarClose = (_, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackBarData((prev) => ({ ...prev, open: false }));
    };

    function dispatchTerminalWatchlistPublicationObservation(payload) {
        const lifecycle = normalizeWatchlistLifecycle(payload);
        if (lifecycle.status !== 'succeeded') {
            throw watchlistApplyError({
                code: WATCHLIST_PUBLICATION_CONTRACT_ERROR_CODE,
                message: 'Watchlist Apply returned an unsupported normalized status.',
            }, WATCHLIST_PUBLICATION_VERIFICATION_MESSAGE);
        }
        const publicationReceipt = parseWatchlistPublicationReceipt(
            lifecycle.terminal_receipt,
        );
        if (!publicationReceipt) {
            throw watchlistApplyError(
                new Error('Watchlist Apply returned an invalid publication receipt.'),
                WATCHLIST_PUBLICATION_VERIFICATION_MESSAGE,
            );
        }
        const classificationResult = parseWatchlistDeltaClassification(
            lifecycle.terminal_receipt,
        );
        if (classificationResult.error) {
            const retryObservation = canonicalWatchlistPublicationObservation(
                lifecycle.terminal_receipt,
            );
            dispatchWatchlistRevisionProgressEvent({
                ...retryObservation,
                reason: 'watchlist_upload_apply_terminal_verification',
                watchlist_revision_id: publicationReceipt.revisionId,
                revision_seq: publicationReceipt.revisionSeq,
            }, sessionScopeToken);
            throw watchlistApplyError(
                new Error(classificationResult.error),
                WATCHLIST_PUBLICATION_VERIFICATION_MESSAGE,
            );
        }
        if (
            classificationResult.classification
            !== WATCHLIST_DELTA_CLASSIFICATIONS.MEMBERSHIP_CHANGED
        ) {
            throw watchlistApplyError({
                code: WATCHLIST_PUBLICATION_CONTRACT_ERROR_CODE,
                message: 'Watchlist Apply returned a non-membership mutation.',
            }, WATCHLIST_PUBLICATION_VERIFICATION_MESSAGE);
        }
        const observation = canonicalWatchlistPublicationObservation(
            lifecycle.terminal_receipt,
        );
        dispatchWatchlistRevisionProgressEvent({
            ...lifecycle,
            ...observation,
            reason: 'watchlist_upload_apply',
            watchlist_revision_id: publicationReceipt.revisionId,
            revision_seq: publicationReceipt.revisionSeq,
            ui_commit_complete: false,
        }, sessionScopeToken, WATCHLIST_REVISION_PROGRESS_OBSERVED_EVENT);
    }

    function buildInputValidationSummaryMessage(inputValidation) {
        const errorCount = Number(inputValidation?.error_count || 0);
        // The column check leaves out columns with no header and no values,
        // so only blank rows are left to report.
        const blankRows = Number(inputValidation?.blank_row_count_removed || 0);
        const cleanup = blankRows > 0
            ? `Import cleanup: ${blankRows} fully blank row${blankRows === 1 ? '' : 's'} ignored.`
            : null;
        // The server names why rows were skipped whenever it skips any.
        const rowSummary = errorCount > 0 && inputValidation?.summary_message
            ? String(inputValidation.summary_message)
            : null;
        return [rowSummary, cleanup, restoredLeadingZerosNote(inputValidation)]
            .filter(Boolean).join(' ') || null;
    }

    function buildIdentityResolutionSummaryMessage(identityResolution) {
        const errorCount = Number(identityResolution?.error_count || 0);
        if (errorCount <= 0) {
            return null;
        }
        const noun = errorCount === 1 ? 'identifier' : 'identifiers';
        const verb = errorCount === 1 ? 'could not be resolved and was' : 'could not be resolved and were';
        return `${errorCount} ${noun} ${verb} skipped. Review Upload Issues under Settings → Security Resolution.`;
    }

    function getIdentityResolutionErrors(identityResolution) {
        return Array.isArray(identityResolution?.errors) ? identityResolution.errors : [];
    }

    function getIdentityResolutionDisplayValue(entry) {
        const rawValue = entry?.input_value_raw ?? entry?.raw_value ?? entry?.input_value_norm;
        return rawValue == null ? '' : String(rawValue).trim();
    }

    function buildBlockedUploadFriendlyMessage(res) {
        const identityResolution = res?.identity_resolution || {};
        const unresolvedCount = Number(identityResolution?.unresolved_count || 0);
        const ambiguousCount = Number(identityResolution?.ambiguous_count || 0);
        const errorCount = Number(
            identityResolution?.error_count || unresolvedCount + ambiguousCount
        );
        const firstIssueValue = getIdentityResolutionErrors(identityResolution)
            .map(getIdentityResolutionDisplayValue)
            .find(Boolean);
        const firstIssueSuffix = firstIssueValue ? ` (${firstIssueValue})` : '';
        const contractStatus = String(res?.status || '').toUpperCase();
        if (contractStatus === 'IDENTITY_UNRESOLVED_ALL') {
            return (
                'No identifiers in this watchlist could be resolved. '
                + 'Your existing watchlist was not changed. Review Upload Issues under Settings → Security Resolution, then retry.'
            );
        }
        const parts = [];
        if (ambiguousCount > 0) {
            parts.push(`${ambiguousCount} need${ambiguousCount === 1 ? 's' : ''} review`);
        }
        if (unresolvedCount > 0) {
            parts.push(`${unresolvedCount} could not be resolved${firstIssueSuffix}`);
        }
        const detail = parts.length > 0 ? parts.join(' and ') : `${errorCount} identifier(s) need attention`;
        return (
            `Watchlist upload paused: ${detail}. `
            + 'Your existing watchlist was not changed. Review Upload Issues under Settings → Security Resolution, then retry.'
        );
    }

    // Shows a recreated review, or closes it when nothing is left to apply.
    function showRecreatedReview(outcome, notice = outcome.changed ? REVIEW_UPDATED_NOTICE : '') {
        if (!outcome.review) {
            setPendingUploadReview(null);
            setUploadReviewDialogOpen(false);
            setSnackBarData({ open: true, severity: 'info', message: outcome.message });
            onReviewAlreadyCurrent?.();
            return;
        }
        setPendingUploadReview(outcome.review);
        onReviewRevisionChanged?.(outcome.review.watchlist_revision_id || outcome.review.id);
        if (notice) setUploadReviewNotice({ severity: 'info', message: notice });
    }

    // Recreates on request a review that is still out of date: an automatic
    // attempt failed, or it opened from another screen without one.
    async function handleRecreateReview() {
        if (!pendingUploadReview?.id || uploadReviewBusy) return;
        setUploadReviewBusy(true);
        setUploadReviewError('');
        try {
            showRecreatedReview(await recreateOutdatedReview(pendingUploadReview, reviewRecreateRef));
        } catch (error) {
            setUploadReviewError(error.message);
        } finally {
            setUploadReviewBusy(false);
        }
    }

    async function handleApplyPendingUpload(importRun) {
        const revisionId = importRun?.watchlist_revision_id || importRun?.id;
        if (!revisionId) {
            setUploadReviewError('Missing staged upload revision id.');
            return;
        }
        if (
            !stagedUploadPendingReview(importRun)
            && !stagedUploadRetryable(importRun)
        ) {
            setUploadReviewError(
                'This accepted upload is already being processed by MSI v2.',
            );
            return;
        }
        const expectedWatchlistRevisionId = (
            importRun?.base_revision_id
            || importRun?.source_metadata_json?.base_revision_id
            || null
        );
        setUploadReviewBusy(true);
        setUploadReviewError('');
        const attemptId = newAttemptId('watchlist-upload-apply');
        try {
            const duplicateOfCurrent = Boolean(
                importRun?.duplicate_of_current
                || importRun?.source_metadata_json?.duplicate_of_current,
            );
            if (duplicateOfCurrent) {
                // Staging compares the complete submitted row set with the
                // current immutable revision before MSI work is authorized.
                // An exact match has no work to publish, so terminalize the
                // pending review without creating a parent operation or
                // painting a synthetic processing lifecycle.
                const duplicateResult = await ignoreWatchlistRevision({
                    revisionId,
                    attemptId: newAttemptId('watchlist-upload-unchanged'),
                    disposition: 'unchanged_duplicate',
                });
                if (!duplicateResult?.success) {
                    throw new Error(
                        duplicateResult?.message
                        || 'Failed to close the unchanged watchlist upload.',
                    );
                }
                setPendingUploadReview(null);
                setUploadReviewDialogOpen(false);
                showCompletedResult(importRun);
                onReviewAlreadyCurrent?.();
                return;
            }
            const res = await applyWatchlistRevision({
                revisionId,
                attemptId,
                expectedWatchlistRevisionId,
                ...(Number(
                    importRun?.diff_summary_json?.portfolio_impact?.security_count || 0,
                ) > 0 ? { portfolioRemovalDecision: 'accept_removals' } : {}),
            });
            if (res?.code === WATCHLIST_STATE_STALE_CODE && recreatableOnApply(importRun)) {
                // The Watchlist changed while this review was open: prepare it
                // again for the user to check before applying.
                try {
                    showRecreatedReview(
                        await recreateOutdatedReview(importRun, reviewRecreateRef),
                        APPLY_AGAIN_NOTICE,
                    );
                } catch (error) {
                    // It can't be applied as it is: offer to try again.
                    setPendingUploadReview({
                        ...importRun,
                        draft_readiness: {
                            ...importRun.draft_readiness,
                            can_apply: false,
                            can_recreate: true,
                            message: OUTDATED_REVIEW_MESSAGE,
                        },
                    });
                    throw error;
                }
                return;
            }
            if (res?.code === WATCHLIST_FACTOR_SOURCE_UNAVAILABLE_CODE) {
                // Published after the last context check: nothing was applied
                // and the review stays open for another try.
                setUploadReviewNotice({
                    severity: 'warning',
                    message: res.message || FACTOR_SOURCE_UNAVAILABLE_MESSAGE,
                });
                return;
            }
            if (!res?.success) {
                throw watchlistApplyError(
                    res,
                    'Failed to apply staged watchlist upload.',
                );
            }
            const lifecycle = normalizeWatchlistLifecycle(res);
            const normalizedApplyStatus = lifecycle.status;
            const processingApply = ['queued', 'running']
                .includes(normalizedApplyStatus);
            const terminalApply = normalizedApplyStatus === 'succeeded';
            if (!processingApply && !terminalApply) {
                throw watchlistApplyError({
                    code: WATCHLIST_PUBLICATION_CONTRACT_ERROR_CODE,
                    message: 'Watchlist Apply returned an unsupported normalized status.',
                }, WATCHLIST_PUBLICATION_VERIFICATION_MESSAGE);
            }
            const applyReceipt = parseWatchlistPublicationReceipt(lifecycle);
            if (!applyReceipt) {
                throw watchlistApplyError(
                    new Error('Watchlist Apply returned an invalid publication receipt.'),
                    'Failed to apply staged watchlist upload.',
                );
            }
            if (processingApply) {
                setPendingUploadReview(null);
                setUploadReviewDialogOpen(false);
                setSnackBarData({
                    open: true,
                    message: 'Watchlist upload accepted. MSI v2 resolution has started; your active Watchlist will update automatically when it is ready.',
                    severity: 'info',
                    revisionLink: res?.watchlist_revision_id ? {
                        revisionId: String(res.watchlist_revision_id),
                        revisionSeq: res?.revision_seq ?? null,
                    } : null,
                });
                dispatchWatchlistRevisionProgressEvent({
                    ...lifecycle,
                    reason: 'watchlist_upload_apply',
                    watchlist_revision_id: applyReceipt.revisionId,
                    revision_seq: applyReceipt.revisionSeq,
                }, sessionScopeToken);
                return;
            }
            setPendingUploadReview(null);
            setUploadReviewDialogOpen(false);
            if (isNothingMatched(lifecycle)) {
                // The import matched nothing and the Watchlist is unchanged: a
                // plain outcome, which the dashboard announces with its counts.
                dispatchWatchlistRevisionProgressEvent({
                    ...lifecycle,
                    reason: 'watchlist_upload_apply',
                    watchlist_revision_id: applyReceipt.revisionId,
                    revision_seq: applyReceipt.revisionSeq,
                }, sessionScopeToken);
                return;
            }
            setSnackBarData({
                open: true,
                message: 'Watchlist upload applied. Market data and downstream state are updating.',
                severity: 'success',
                revisionLink: res?.watchlist_revision_id ? {
                    revisionId: String(res.watchlist_revision_id),
                    revisionSeq: res?.revision_seq ?? null,
                } : null,
            });
            dispatchTerminalWatchlistPublicationObservation(lifecycle);
        } catch (err) {
            const message = err?.message || 'Failed to apply staged watchlist upload.';
            if (err?.code === WATCHLIST_PUBLICATION_VERIFICATION_ERROR_CODE) {
                setPendingUploadReview(null);
                setUploadReviewDialogOpen(false);
                setUploadReviewError('');
                setSnackBarData({ open: true, message, severity: 'info' });
            } else {
                setUploadReviewError(message);
                setSnackBarData({ open: true, message, severity: 'error' });
            }
        } finally {
            setUploadReviewBusy(false);
        }
    }

    async function handleIgnorePendingUpload(importRun) {
        const revisionId = importRun?.watchlist_revision_id || importRun?.id;
        if (!revisionId) {
            setUploadReviewError('Missing staged upload revision id.');
            return;
        }
        setUploadReviewBusy(true);
        setUploadReviewError('');
        const attemptId = newAttemptId('watchlist-upload-ignore');
        try {
            const res = await ignoreWatchlistRevision({ revisionId, attemptId });
            if (!res?.success) {
                throw new Error(res?.message || 'Could not discard this Watchlist change.');
            }
            setPendingUploadReview(null);
            setUploadReviewDialogOpen(false);
            setSnackBarData({
                open: true,
                message: 'Watchlist change discarded. Your active Watchlist is unchanged.',
                severity: 'info',
            });
            dispatchWatchlistRevisionClosedEvent(revisionId, sessionScopeToken);
            onReviewIgnored?.(revisionId);
        } catch (err) {
            const message = err?.message || 'Could not discard this Watchlist change.';
            setUploadReviewError(message);
            setSnackBarData({ open: true, message, severity: 'error' });
        } finally {
            setUploadReviewBusy(false);
        }
    }

    async function handleKeepPendingUploadSecurities(importRun) {
        const revisionId = importRun?.watchlist_revision_id || importRun?.id;
        if (!revisionId) {
            setUploadReviewError('Missing staged upload revision id.');
            return;
        }
        const expectedWatchlistRevisionId = (
            importRun?.base_revision_id
            || importRun?.source_metadata_json?.base_revision_id
            || null
        );
        setUploadReviewBusy(true);
        setUploadReviewError('');
        setUploadReviewNotice(null);
        try {
            const res = await keepWatchlistPortfolioSecurities({
                revisionId,
                attemptId: newAttemptId('watchlist-upload-keep'),
                expectedWatchlistRevisionId,
            });
            if (!res?.success) {
                throw new Error(
                    res?.message || 'Failed to keep the affected securities in the staged Watchlist.',
                );
            }
            if (importRun?.source_metadata_json?.membership_removal_review_contract
                    === 'watchlist-membership-removal-review.v1'
                && res.review_closed === true && res.watchlist_changed === false
                && res.watchlist_revision_id === revisionId) {
                setPendingUploadReview(null);
                setUploadReviewDialogOpen(false);
                setSnackBarData({ open: true, severity: 'info',
                    message: 'Kept the securities; no Watchlist changes applied.' });
                return;
            }
            const refreshedReview = normalizeUploadReview(res);
            if (!refreshedReview) {
                throw new Error('The updated Watchlist review response was invalid.');
            }
            setPendingUploadReview(refreshedReview);
            const keptCount = Array.isArray(res?.kept_cognive_ids) ? res.kept_cognive_ids.length : 0;
            const kept = keptCount > 0
                ? `${keptCount} ${keptCount === 1 ? 'security' : 'securities'} you hold`
                : 'the securities you hold';
            setUploadReviewNotice({
                severity: 'success',
                message: `Added ${kept} back to this change, so it no longer removes them. Review the updated list, then apply.`,
            });
        } catch (err) {
            const message = err?.message
                || 'Failed to keep the affected securities in the staged Watchlist.';
            setUploadReviewError(message);
            setSnackBarData({ open: true, message, severity: 'error' });
        } finally {
            setUploadReviewBusy(false);
        }
    }

    async function reviewFileColumns(matrix) {
        const res = await reviewWatchlistFileColumns(matrix);
        const review = res?.success ? watchlistColumnReview(res.column_review) : null;
        if (!review) {
            throw new Error(res?.message || "The file's columns could not be checked. Try again.");
        }
        return review;
    }

    async function handleFileUpload(prepared) {
        const params = {
            upload_attempt_id: pendingUploadAttemptIdRef.current || newUploadAttemptId(),
            matrix: prepared.matrix,
            column_selection: prepared.columnSelection,
            default_target_vol_pct: resolvedDefaultTargetVolPct,
            default_target_vol_dollar: resolvedDefaultTargetVolDollar,
            default_scaler: resolvedDefaultScaler,
        };
        pendingUploadAttemptIdRef.current = params.upload_attempt_id;
        try {
            const res = await processWatchlistFile(params);
            const inputValidationPayload = res?.input_validation || null;
            const inputValidationSummary = buildInputValidationSummaryMessage(inputValidationPayload);
            // A business rejection is definitive even when the server returns
            // it as a 200 envelope. Keep the UUID only when the outcome is
            // genuinely ambiguous (transport/no response or 5xx).
            if (
                res?.success
                || res?.status === 'INPUT_VALIDATION_FAILED'
                || (Number(res?.status_code) >= 400 && Number(res?.status_code) < 500)
            ) {
                pendingUploadAttemptIdRef.current = null;
            }
            if (
                res?.success
                && res?.status === 'unchanged'
                && res?.changed === false
            ) {
                setPendingUploadReview(null);
                setUploadReviewDialogOpen(false);
                setUploadReviewError('');
                showCompletedResult({ ...res, note: inputValidationSummary });
                setDuplicateSedols([]);
                return { kind: FILE_UPLOAD_RESULT_KINDS.UNCHANGED };
            }
            if (
                res?.success
                && res?.status === 'existing_work'
                && res?.existing_work === true
            ) {
                setPendingUploadReview(null);
                setUploadReviewDialogOpen(false);
                setUploadReviewError('');
                setSnackBarData({
                    open: true,
                    message: [
                        res?.message || 'An equivalent Watchlist change is already awaiting review.',
                        inputValidationSummary,
                    ].filter(Boolean).join(' '),
                    severity: 'info',
                    revisionLink: res?.watchlist_revision_id ? {
                        revisionId: String(res.watchlist_revision_id),
                        revisionSeq: res?.revision_seq ?? null,
                    } : null,
                });
                setDuplicateSedols([]);
                return { kind: FILE_UPLOAD_RESULT_KINDS.UNCHANGED };
            }
            const stagedReview = extractUploadReview(res);
            const duplicateOfCurrent = isDuplicateOfCurrentWatchlistRevision(res);
            const isInputValidationFullRejection = Boolean(
                res
                && !res.success
                && (res.status === 'INPUT_VALIDATION_FAILED' || res?.input_validation?.full_rejection),
            );
            const identityResolutionSummary = buildIdentityResolutionSummaryMessage(res?.identity_resolution);
            if (isInputValidationFullRejection) {
                setSkippedRowsDialog({ open: true, payload: inputValidationPayload });
                setDuplicateSedols([]);
                return {
                    kind: FILE_UPLOAD_RESULT_KINDS.REJECTED,
                    message: inputValidationSummary
                        || inputValidationPayload?.summary_message
                        || 'The selected watchlist file was rejected.',
                };
            }

            if (!res?.success && showWatchlistDraftGuidance(res, sessionScopeToken)) {
                return { kind: FILE_UPLOAD_RESULT_KINDS.REJECTED, message: res.message };
            }

            if (res && res.success) {
                if (!stagedReview) {
                    throw new Error('Watchlist upload succeeded but no staged review was returned.');
                }
                if (duplicateOfCurrent) {
                    const revisionId = (
                        stagedReview.watchlist_revision_id
                        || stagedReview.id
                        || res.watchlist_revision_id
                    );
                    const duplicateResult = await ignoreWatchlistRevision({
                        revisionId,
                        attemptId: newAttemptId('watchlist-upload-unchanged'),
                        disposition: 'unchanged_duplicate',
                    });
                    if (!duplicateResult?.success) {
                        throw new Error(
                            duplicateResult?.message
                            || 'Failed to close the unchanged watchlist upload.',
                        );
                    }
                    setPendingUploadReview(null);
                    setUploadReviewDialogOpen(false);
                    setUploadReviewError('');
                    showCompletedResult({ ...stagedReview,
                        input_validation: inputValidationPayload, note: inputValidationSummary });
                    setDuplicateSedols([]);
                    return { kind: FILE_UPLOAD_RESULT_KINDS.UNCHANGED };
                }
                if (res?.equivalent_pending === true) {
                    const pendingReview = stagedUploadPendingReview(stagedReview);
                    const retryable = stagedUploadRetryable(stagedReview);
                    if (pendingReview || retryable) {
                        setPendingUploadReview(stagedReview);
                        setUploadReviewDialogOpen(true);
                        setUploadReviewError('');
                        setSnackBarData({
                            open: true,
                            message: pendingReview
                                ? 'This Watchlist change is already awaiting review.'
                                : 'This Watchlist operation needs attention and can be retried safely.',
                            severity: retryable ? 'warning' : 'info',
                        });
                        setDuplicateSedols([]);
                        return { kind: FILE_UPLOAD_RESULT_KINDS.STAGED };
                    }
                    const workStatus = String(
                        stagedReview?.lifecycle?.status
                        || res?.lifecycle?.status
                        || res?.status
                        || 'in_progress',
                    ).replaceAll('_', ' ');
                    setPendingUploadReview(null);
                    setUploadReviewDialogOpen(false);
                    setUploadReviewError('');
                    setSnackBarData({
                        open: true,
                        message: `This Watchlist change already exists and is ${workStatus}.`,
                        severity: 'info',
                        revisionLink: res?.watchlist_revision_id ? {
                            revisionId: String(res.watchlist_revision_id),
                            revisionSeq: res?.revision_seq ?? stagedReview?.revision_seq ?? null,
                        } : null,
                    });
                    setDuplicateSedols([]);
                    return { kind: FILE_UPLOAD_RESULT_KINDS.UNCHANGED };
                }
                const msiV2Pending = isMsiV2StagedUpload(stagedReview);
                const drasticChangeWarning = Boolean(
                    stagedReview?.drastic_change_warning
                    || stagedReview?.diff_summary_json?.drastic_change
                );
                const provisionalMsiV2Warning = Boolean(
                    msiV2Pending
                    && (
                        stagedUploadPendingReview(stagedReview)
                        || stagedReview?.diff_summary_json?.provisional === true
                    )
                );
                const showDrasticChangeWarning = (
                    drasticChangeWarning && !provisionalMsiV2Warning
                );
                const processedCount = Number(res?.identity_resolution?.resolved_count || 0) > 0
                    ? Number(res.identity_resolution.resolved_count)
                    : Number(res?.input_validation?.row_count_valid || 0);
                const securityNoun = processedCount === 1 ? 'security' : 'securities';
                const baseSuccess = msiV2Pending
                    ? (
                        processedCount > 0
                            ? `Watchlist upload staged. Review the expected changes for ${processedCount} ${securityNoun}. MSI v2 has not started.`
                            : 'Watchlist upload staged for review. MSI v2 has not started.'
                    )
                    : (
                        processedCount > 0
                            ? `Watchlist upload staged. ${processedCount} ${securityNoun} ready for review.`
                            : 'Watchlist upload staged for review.'
                    );
                const messageParts = [baseSuccess];
                if (inputValidationSummary) {
                    messageParts.push(inputValidationSummary);
                }
                if (identityResolutionSummary) {
                    messageParts.push(identityResolutionSummary);
                }
                if (showDrasticChangeWarning) {
                    messageParts.push('Review the large Watchlist change before applying.');
                }
                const finalMessage = msiV2Pending
                    ? `${messageParts.join(' ')} Apply to accept the upload and begin resolution.`
                    : `${messageParts.join(' ')} Apply it to update your active Watchlist.`;
                let severity;
                if (
                    inputValidationSummary
                    || identityResolutionSummary
                    || showDrasticChangeWarning
                ) {
                    severity = 'warning';
                } else {
                    severity = 'info';
                }
                const uploadReview = extractUploadReview(res);
                setPendingUploadReview(uploadReview);
                setUploadReviewDialogOpen(Boolean(
                    uploadReview
                    && (
                        !isMsiV2StagedUpload(uploadReview)
                        || stagedUploadPendingReview(uploadReview)
                    )
                ));
                setUploadReviewError('');
                setSnackBarData({
                    open: true,
                    message: finalMessage,
                    severity,
                    revisionLink: res?.watchlist_revision_id ? {
                        revisionId: String(res.watchlist_revision_id),
                        revisionSeq: res?.revision_seq ?? null,
                    } : null,
                });
                dispatchWatchlistRevisionProgressEvent({
                    ...res,
                    watchlist_revision_id: String(
                        res.watchlist_revision_id || stagedReview.id || '',
                    ),
                }, sessionScopeToken);
                setDuplicateSedols(res.duplicate_sedols || res.duplicate_tickers || []);
                return { kind: FILE_UPLOAD_RESULT_KINDS.STAGED };
            } else if (res && res.identity_resolution) {
                const friendlyMessage = buildBlockedUploadFriendlyMessage(res);
                const combinedMessage = inputValidationSummary
                    ? `${friendlyMessage} Additionally, ${inputValidationSummary}`
                    : friendlyMessage;
                setSnackBarData({
                    open: true,
                    message: combinedMessage,
                    severity: 'info',
                });
                setDuplicateSedols([]);
                return { kind: FILE_UPLOAD_RESULT_KINDS.REJECTED, message: combinedMessage };
            } else {
                setSnackBarData({ open: true, message: res?.message || 'Failed to process watchlist file on the server.', severity: 'error' });
                throw new Error(res?.message || 'Failed to process watchlist file on the server.');
            }
        } catch (err) {
            console.error('Upload error:', err);
            const uploadError = err instanceof Error
                ? err
                : new Error('Failed to process watchlist file on the server.');
            setSnackBarData({
                open: true,
                message: uploadError.message,
                severity: 'error',
            });
            setDuplicateSedols([]);
            // Let FileUploader retain the selected file and render the same
            // actionable error beside the Upload control. A rejected staging
            // request must never be presented as a successful upload.
            return { kind: FILE_UPLOAD_RESULT_KINDS.FAILED, message: uploadError.message };
        }
    }
    const watchlistUploadInfo = (
        <>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                Upload a CSV or Excel file
                {watchlistRequiredColumns.length > 0
                    ? <> with required columns <strong>{watchlistRequiredColumns.join(', ')}</strong></>
                    : null}
                <> and one explicit identifier: <strong>{MSI_V2_STAGED_IDENTIFIER_COLUMNS.join(', ')}</strong>. Ticker is treated only as a display label when another identifier is selected. Country and MIC are optional identity qualifiers; omitted Country follows MSI's current namespace policy and unresolved or ambiguous results remain available for review.</>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                Every other non-identifier, non-qualifier, non-beta column becomes an optional Watchlist attribute. Attribute cells may be left blank and edited later.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                Optional watchlist beta columns can be named <strong>beta</strong> or <strong>beta_&lt;source&gt;</strong>.
                Alphamizer reads up to 5 beta columns per upload and adds each one as a selectable beta source.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                Examples: <strong>beta_BBG</strong>, <strong>beta_factset</strong>, and <strong>beta</strong>.
                These will appear as <strong>BBG (via watchlist)</strong>, <strong>factset (via watchlist)</strong>, and <strong>&lt;unnamed&gt; (via watchlist)</strong>.
            </Typography>
        </>
    );
    const uploaderContent = (
        <>
            {!reviewOnly && <FileUploader
                onColumnReviewChange={(open) => {
                    columnReviewActiveRef.current = open;
                    if (open) setUploadReviewDialogOpen(false);
                }}
                onFileUpload={handleFileUpload} 
                reviewColumns={reviewFileColumns}
                uploaderKey={WATCHLIST_KEY}
                requiredColumns={watchlistRequiredColumns}
                labelName='Watchlist'
                helperText="You can add up to 5 watchlist beta columns named beta or beta_<source>."
                infoPopoverContent={watchlistUploadInfo}
                compact
                compactVariant={variant === 'header' ? 'dropdown' : 'inline'}
                showTitle={false}
                onInteraction={onInteraction}
            />}
            <Snackbar
                open={snackBarData.open}
                autoHideDuration={8000}
                onClose={handleSnackbarClose}
            >
                <Alert
                    severity={snackBarData.severity}
                    action={snackBarData.revisionLink ? (
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => dispatchWatchlistHistoryOpenEvent(
                                snackBarData.revisionLink,
                                sessionScopeToken,
                            )}
                            sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
                        >
                            View changes
                            {snackBarVersion.revisionId === snackBarRevisionId
                                ? snackBarVersion.suffix
                                : ''}
                        </Button>
                    ) : null}
                >
                    {snackBarData.message}
                </Alert>
            </Snackbar>
            <Dialog
                open={Boolean(
                    uploadReviewDialogOpen
                    && pendingUploadReview
                    && (
                        stagedUploadPendingReview(pendingUploadReview)
                        || stagedUploadRetryable(pendingUploadReview)
                    )
                )}
                onClose={(_, reason) => {
                    if (uploadReviewBusy || reason === 'backdropClick') {
                        return;
                    }
                    setUploadReviewDialogOpen(false);
                }}
                maxWidth="lg"
                fullWidth
                aria-labelledby="watchlist-upload-review-title"
            >
                <DialogTitle id="watchlist-upload-review-title">
                    Review Watchlist Change
                </DialogTitle>
                <DialogContent dividers>
                    {uploadReviewError && (
                        <Alert severity="error" sx={{ mb: 1.5 }}>
                            {uploadReviewError}
                        </Alert>
                    )}
                    {uploadReviewNotice && (
                        <Alert
                            severity={uploadReviewNotice.severity}
                            sx={{ mb: 1.5 }}
                            onClose={() => setUploadReviewNotice(null)}
                        >
                            {uploadReviewNotice.message}
                        </Alert>
                    )}
                    {isMsiV2StagedUpload(pendingUploadReview) && (() => {
                        const pendingReview = stagedUploadPendingReview(pendingUploadReview);
                        const counts = stagedResolutionCounts(pendingUploadReview);
                        const retryable = stagedUploadRetryable(pendingUploadReview);
                        const hasReviewItems = (
                            counts.ambiguous > 0
                            || counts.unresolved > 0
                            || counts.labelsPending > 0
                            || counts.duplicates > 0
                        );
                        if (pendingReview) return null;
                        return (
                            <Alert
                                severity={(retryable || hasReviewItems) ? 'warning' : 'info'}
                                sx={{ mb: 1.5 }}
                            >
                                {retryable
                                    ? 'MSI v2 processing stopped before completion. Your active Watchlist is unchanged. Apply retries the same accepted upload safely.'
                                    : (
                                        <>
                                            MSI v2 resolution is in progress. Processed: {counts.processed || counts.resolved} of {counts.rows}
                                            {counts.rows > 0 ? ` (${counts.progress || Math.round(((counts.processed || counts.resolved) / counts.rows) * 100)}%).` : '.'}
                                            {counts.ambiguous > 0 && ` Needs review: ${counts.ambiguous}.`}
                                            {counts.unresolved > 0 && ` Unresolved: ${counts.unresolved}.`}
                                            {counts.labelsPending > 0 && ` Labels pending: ${counts.labelsPending}.`}
                                            {counts.duplicates > 0 && ` Duplicate identities: ${counts.duplicates}.`}
                                            {' '}Your active Watchlist has not changed.
                                        </>
                                    )}
                            </Alert>
                        );
                    })()}
                    {pendingUploadReview?.draft_readiness?.can_apply === false && (
                        <Alert severity="info" sx={{ mb: 1.5 }} action={
                            pendingUploadReview?.draft_readiness?.can_recreate
                                ? <Button disabled={uploadReviewBusy} onClick={handleRecreateReview}>Recreate review</Button>
                                : null
                        }>{pendingUploadReview.draft_readiness.message}</Alert>
                    )}
                    {factorSourceBlocksApply && (
                        <Alert severity="info" sx={{ mb: 1.5 }}>
                            {FACTOR_SOURCE_UNAVAILABLE_MESSAGE}
                        </Alert>
                    )}
                    <PendingImportCard
                        importRun={pendingUploadReview}
                        title={pendingUploadReview?.source_metadata_json?.combined_draft_contract
                            ? 'Pending Watchlist Changes'
                            : pendingUploadReview?.change_source === 'restore'
                                ? 'Pending Watchlist Restore'
                                : pendingUploadReview?.source_metadata_json?.ingress_kind === 'm365_excel'
                            ? 'Pending Watchlist Import'
                            : 'Pending Watchlist Upload'}
                        onApply={handleApplyPendingUpload}
                        onIgnore={handleIgnorePendingUpload}
                        onKeepInWatchlist={handleKeepPendingUploadSecurities}
                        applying={uploadReviewBusy}
                        diffDetailsMode="watchlist_upload"
                        visibleCountKeys={['added', 'removed', 'changed', 'unchanged']}
                        visibleDiffSections={['added', 'removed', 'changed', 'ignored']}
                        applyDisabled={pendingUploadReview?.draft_readiness?.can_apply === false || factorSourceBlocksApply || Boolean(
                            isMsiV2StagedUpload(pendingUploadReview)
                            && !stagedUploadPendingReview(pendingUploadReview)
                            && !stagedUploadRetryable(pendingUploadReview)
                        )}
                        applyDisabledReason={pendingUploadReview?.draft_readiness?.message
                            || (factorSourceBlocksApply ? FACTOR_SOURCE_UNAVAILABLE_MESSAGE : '')
                            || "This accepted change is already being processed."}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setUploadReviewDialogOpen(false)}
                        disabled={uploadReviewBusy}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <WatchlistCompletedResultDialog result={completedResult}
                onClose={() => setCompletedResult(null)} />
            <SkippedRowsDialog
                open={skippedRowsDialog.open}
                title={skippedRowsDialog.title || 'Watchlist upload could not be processed'}
                lead={skippedRowsDialog.lead}
                inputValidation={skippedRowsDialog.payload}
                onClose={() => setSkippedRowsDialog((dialog) => ({ ...dialog, open: false }))}
            />
        </>
    );

    if (reviewOnly || variant === 'header' || variant === 'expanded') {
        return uploaderContent;
    }

    return (
        <Grid2 size={{ xs: 12, md: 6 }}>
            <Paper className={cardClasses} elevation={1} sx={watchlistPanelSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CloudUploadOutlinedIcon fontSize="small" color="action" />
                        <Typography
                            variant="subtitle1"
                            sx={sectionTitleSx}
                        >
                            Watchlist Upload
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Chip size="small" variant="outlined" label="Manual Upload" />
                        <Chip size="small" variant="outlined" label="Replace Watchlist" />
                    </Box>
                </Box>
                {uploaderContent}
            </Paper>
        </Grid2>
    );
}

WatchlistUploader.propTypes = {
    active: PropTypes.bool,
    reviewOnly: PropTypes.bool,
    onReviewIgnored: PropTypes.func,
    onReviewRevisionChanged: PropTypes.func,
    onReviewAlreadyCurrent: PropTypes.func,
    externalReviewHandoff: PropTypes.shape({
        commandId: PropTypes.number,
        pendingRevision: PropTypes.object,
        revisionId: PropTypes.string,
        revisionSeq: PropTypes.number,
        sessionScopeToken: PropTypes.string,
        status: PropTypes.string,
    }),
    onExternalReviewHandoffConsumed: PropTypes.func,
};
